import { useParams, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import Sidebar from "../components/Sidebar";
import Topbar from "../components/Topbar";
import { useAuth } from "../context/AuthContext";
import { API_BASE } from "../utils/Instance";
import { toast } from "sonner";

// Countdown Timer Component
function CountdownTimer({ targetTime, label = "Contest Starts In" }) {
  const [timeLeft, setTimeLeft] = useState(getTimeLeft(targetTime));

  function getTimeLeft(time) {
    if (!time) return 0;
    const diff = new Date(time) - new Date();
    return diff;
  }

  function formatTime(ms) {
    if (ms <= 0) return "Starting now...";
    const totalSeconds = Math.floor(ms / 1000);
    const days = Math.floor(totalSeconds / (3600 * 24));
    const hours = Math.floor((totalSeconds % (3600 * 24)) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return `${days > 0 ? `${days}d ` : ""}${String(hours).padStart(
      2,
      "0"
    )}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  }

  useEffect(() => {
    const timer = setInterval(() => {
      const remaining = getTimeLeft(targetTime);
      setTimeLeft(remaining);
    }, 1000);
    return () => clearInterval(timer);
  }, [targetTime]);

  return (
    <div className="text-center">
      <p className="text-sm text-gray-400 mb-2">{label}</p>
      <p className="text-4xl font-bold text-[#ceb46a]">{formatTime(timeLeft)}</p>
    </div>
  );
}

export default function WaitingRoom() {
  const { contestId } = useParams();
  const navigate = useNavigate();
  const { profile } = useAuth();
  const [contest, setContest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasStarted, setHasStarted] = useState(false);

  useEffect(() => {
    const fetchContest = async () => {
      try {
        const token = profile?.accessToken;
        if (!token) {
          toast.error("Please login to view contest");
          navigate("/login");
          return;
        }

        const response = await fetch(`${API_BASE}accounts/contests/${contestId}/`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const contestData = await response.json();
          setContest(contestData);
          
          // Check if contest has started
          const now = new Date();
          const startTime = contestData.start_time ? new Date(contestData.start_time) : null;
          const started = startTime && now >= startTime;
          setHasStarted(started);
          
          // If contest has started, redirect to voting panel
          if (started) {
            navigate(`/photo-voting/${contestId}`);
          }
        } else {
          toast.error("Failed to load contest");
          navigate("/contests");
        }
      } catch (error) {
        console.error("Error fetching contest:", error);
        toast.error("An error occurred");
        navigate("/contests");
      } finally {
        setLoading(false);
      }
    };

    if (contestId && profile?.accessToken) {
      fetchContest();
    }
  }, [contestId, profile?.accessToken, navigate]);

  // Check periodically if contest has started
  useEffect(() => {
    if (!contest?.start_time) return;

    const checkStartTime = () => {
      const now = new Date();
      const startTime = new Date(contest.start_time);
      const started = now >= startTime;
      
      if (started && !hasStarted) {
        setHasStarted(true);
        toast.success("Contest has started! Redirecting to voting panel...");
        setTimeout(() => {
          navigate(`/photo-voting/${contestId}`);
        }, 1000);
      }
    };

    // Check immediately
    checkStartTime();

    // Check every second
    const interval = setInterval(checkStartTime, 1000);

    return () => clearInterval(interval);
  }, [contest?.start_time, hasStarted, navigate, contestId]);

  if (loading) {
    return (
      <div className="flex min-h-screen bg-black">
        <Sidebar />
        <div className="flex-1 p-4 md:p-6 lg:ml-64 w-full">
          <Topbar pageTitle="Loading..." />
          <div className="flex items-center justify-center h-64">
            <div className="text-white">Loading contest...</div>
          </div>
        </div>
      </div>
    );
  }

  if (!contest) {
    return null;
  }

  return (
    <div className="flex min-h-screen bg-black">
      <Sidebar />
      <div className="flex-1 p-4 md:p-6 lg:ml-64 w-full">
        <Topbar pageTitle="Waiting Room" />
        
        <div className="max-w-4xl mx-auto mt-8">
          <div className="bg-[#1c1c1e] border border-[#ceb46a] rounded-lg p-8 text-center">
            <div className="mb-6">
              <h1 className="text-3xl font-bold text-[#ceb46a] mb-2">
                {contest.title}
              </h1>
              <p className="text-gray-400">{contest.description || "Get ready to vote!"}</p>
            </div>

            <div className="mb-8">
              <CountdownTimer 
                targetTime={contest.start_time} 
                label="Contest Starts In"
              />
            </div>

            <div className="bg-black rounded-lg p-6 mb-6">
              <h2 className="text-xl font-semibold text-white mb-4">
                Waiting for Contest to Start
              </h2>
              <p className="text-gray-300 mb-4">
                You've successfully joined this contest. The voting will begin when the contest starts.
              </p>
              <p className="text-sm text-gray-400">
                You'll be automatically redirected to the voting panel when the contest goes live.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-center">
              <div className="bg-[#2a2a2a] rounded-lg p-4">
                <p className="text-gray-400 text-sm mb-1">Participants</p>
                <p className="text-2xl font-bold text-[#ceb46a]">
                  {contest.joined || contest.participants_count || 0}
                </p>
              </div>
              <div className="bg-[#2a2a2a] rounded-lg p-4">
                <p className="text-gray-400 text-sm mb-1">Max Participants</p>
                <p className="text-2xl font-bold text-[#ceb46a]">
                  {contest.max_participants || 0}
                </p>
              </div>
            </div>

            <div className="mt-8">
              <button
                onClick={() => navigate("/contests")}
                className="px-6 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
              >
                Back to Contests
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

